import {
  AutoClosingDropdown,
  DateRangeDropdown,
  Dropdown,
  EyeCrossedIcon,
  EyeIcon,
  RecurringIcon,
  SearchBar,
  SmallButton,
  TimePicker,
  UndoIcon,
} from "@wit/mpesa-ui-components";
import styleTheme from "@wit/mpesa-ui-components/lib/configs/theme.config";
import { FilterFuncts } from "@wit/mpesa-ui-components/lib/hooks/use-filters.hook";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { RatioContainer, Row } from "../../../../shared/shared.styled";
import { GatewayLogStatusEnum } from "../../support.interfaces";
import { onEnterPressed } from "../../../../shared/shared.utils";
import { OtherFiltersContainer, SearchBarFilterContainer } from "../../../../shared/responsive-ui.styled";
import { FiltersContainer } from "../../support.styled";

export enum GatewayFilterKeys {
  STATUS = "requestStatus",
  OPERATION = "gwOperation",
  TIME_RANGE = "timeRange",
  START_DATE = "startDate",
  END_DATE = "endDate",

  REQUEST_ID = "requestId",
  REQUEST_CONTENT = "requestContent",
  ASYNC_RESPONSE_CONTENT = "asyncResponseContent",
  SYNC_RESPONSE_CONTENT = "syncResponseContent",
}

interface IGatewayLogsFiltersProps extends FilterFuncts {
  gatewayLogsCount: number;
  refreshResults: () => void;
}

/**
 * Gateway logs filter components
 * @param {any} params
 * @returns {JSX.Element} component
 */
const GatewayLogsFilters = ({
  filters,
  clearFilter,
  isFilterActive,
  resetFilters,
  updateFilter,
  updateMultipleFilters,
  gatewayLogsCount,
  refreshResults,
}: IGatewayLogsFiltersProps) => {
  const [t] = useTranslation();
  const [timeValues, setTimeValues] = React.useState({
    fromValue: "00:00",
    toValue: "18:00",
  });
  const [isTimePickerOpen, setIsTimePickerOpen] = React.useState(false);
  const firstUpdate = React.useRef(true);
  const [showExtraFields, setShowExtraFields] = React.useState(false);

  /*
   * Clear the hidden search fields upon hiding them
   * */
  React.useEffect(() => {
    if (!showExtraFields) {
      updateMultipleFilters([
        { key: GatewayFilterKeys.REQUEST_CONTENT, values: [""] },
        { key: GatewayFilterKeys.ASYNC_RESPONSE_CONTENT, values: [""] },
        { key: GatewayFilterKeys.SYNC_RESPONSE_CONTENT, values: [""] },
      ]);
    }
  }, [showExtraFields]);

  /*
   * Update time range filter upon timeValues change
   * */
  React.useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    updateMultipleFilters([{ key: GatewayFilterKeys.TIME_RANGE, values: [timeValues.fromValue, timeValues.toValue] }]);
  }, [timeValues]);

  return (
    <>
      <FiltersContainer style={{ marginBottom: "16px" }}>
        <SearchBarFilterContainer>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(GatewayFilterKeys.REQUEST_ID)
                ? (filters.get(GatewayFilterKeys.REQUEST_ID) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(GatewayFilterKeys.REQUEST_ID, e.target.value, true)}
            clearValue={() => clearFilter(GatewayFilterKeys.REQUEST_ID)}
            placeholderLabel={t("pages.support.gatewayLogs.filters.requestId")}
          />
        </SearchBarFilterContainer>
        <SearchBarFilterContainer>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(GatewayFilterKeys.OPERATION) ? (filters.get(GatewayFilterKeys.OPERATION) as string[])[0] : ""
            }
            onChange={e => updateFilter(GatewayFilterKeys.OPERATION, e.target.value, true)}
            clearValue={() => clearFilter(GatewayFilterKeys.OPERATION)}
            placeholderLabel={t("pages.support.gatewayLogs.filters.gatewayOperation")}
          />
        </SearchBarFilterContainer>
        <OtherFiltersContainer>
          <DateRangeDropdown
            filters={filters}
            isFilterActive={isFilterActive}
            clearFilter={clearFilter}
            updateFilter={updateFilter}
            updateMultipleFilters={updateMultipleFilters}
          />
        </OtherFiltersContainer>
        <OtherFiltersContainer>
          <Dropdown
            controlledToggleOpen={setIsTimePickerOpen}
            isOpen={isTimePickerOpen}
            hasValue={filters.has(GatewayFilterKeys.TIME_RANGE)}
            label={
              filters.has(GatewayFilterKeys.TIME_RANGE)
                ? `${(filters.get(GatewayFilterKeys.TIME_RANGE) as string[])[0]} - ${
                    (filters.get(GatewayFilterKeys.TIME_RANGE) as string[])[1]
                  }`
                : t("pages.support.gatewayLogs.filters.timeRange")
            }
          >
            {isTimePickerOpen ? (
              <TimePickerContainer>
                <TimePicker values={timeValues} setValues={setTimeValues} />
              </TimePickerContainer>
            ) : null}

            <ClearFilterText
              onClick={() => {
                setTimeValues({
                  fromValue: "00:00",
                  toValue: "18:00",
                });
                setTimeout(() => {
                  setIsTimePickerOpen(false);
                  clearFilter(GatewayFilterKeys.TIME_RANGE);
                }, 0);
              }}
            >
              {t("pages.support.gatewayLogs.filters.clearFilter")}
            </ClearFilterText>
          </Dropdown>
        </OtherFiltersContainer>
        <OtherFiltersContainer>
          <AutoClosingDropdown
            options={Object.values(GatewayLogStatusEnum)
              .filter(s => typeof s === "string")
              .map(status => {
                return {
                  label: t(`commons.gatewayLogStatus.${status}`),
                  key: status,
                };
              })}
            selectOption={opt => {
              updateFilter(GatewayFilterKeys.STATUS, opt.key, true);
            }}
            hasValue={!!filters.get(GatewayFilterKeys.STATUS)}
            label={
              filters.get(GatewayFilterKeys.STATUS)
                ? t(`commons.gatewayLogStatus.${(filters.get(GatewayFilterKeys.STATUS) as string[])[0]}`)
                : t("pages.support.gatewayLogs.filters.status")
            }
          ></AutoClosingDropdown>
        </OtherFiltersContainer>

        {/*<MultipleOptionsDropdown
            options={Object.values(GatewayLogStatusEnum)
              .filter(s => typeof s === "string")
              .map(status => {
                return {
                  label: t(`commons.gatewayLogStatus.${status}`),
                  key: status,
                };
              })}
            toggleOption={opt => updateFilter(GatewayFilterKeys.STATUS, opt.key)}
            isOptionSelected={opt => isFilterActive(GatewayFilterKeys.STATUS, opt.key)}
            clearAllFilters={() => clearFilter(GatewayFilterKeys.STATUS)}
            hasValue={!!filters.get(GatewayFilterKeys.STATUS)}
            label={t("pages.support.gatewayLogs.filters.status")}
          ></MultipleOptionsDropdown>*/}
      </FiltersContainer>

      <FiltersContainer style={{ display: showExtraFields ? "inherit" : "none", marginTop: "0", marginBottom: "16px" }}>
        <SearchBarFilterContainer>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(GatewayFilterKeys.REQUEST_CONTENT)
                ? (filters.get(GatewayFilterKeys.REQUEST_CONTENT) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(GatewayFilterKeys.REQUEST_CONTENT, e.target.value, true)}
            clearValue={() => clearFilter(GatewayFilterKeys.REQUEST_CONTENT)}
            placeholderLabel={t("pages.support.gatewayLogs.filters.requestContent")}
          />
        </SearchBarFilterContainer>
        <SearchBarFilterContainer>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(GatewayFilterKeys.ASYNC_RESPONSE_CONTENT)
                ? (filters.get(GatewayFilterKeys.ASYNC_RESPONSE_CONTENT) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(GatewayFilterKeys.ASYNC_RESPONSE_CONTENT, e.target.value, true)}
            clearValue={() => clearFilter(GatewayFilterKeys.ASYNC_RESPONSE_CONTENT)}
            placeholderLabel={t("pages.support.gatewayLogs.filters.asyncResponseContent")}
          />
        </SearchBarFilterContainer>
        <SearchBarFilterContainer>
          <SearchBar
            onKeyDown={e => {
              onEnterPressed(e, refreshResults);
            }}
            value={
              filters.has(GatewayFilterKeys.SYNC_RESPONSE_CONTENT)
                ? (filters.get(GatewayFilterKeys.SYNC_RESPONSE_CONTENT) as string[])[0]
                : ""
            }
            onChange={e => updateFilter(GatewayFilterKeys.SYNC_RESPONSE_CONTENT, e.target.value, true)}
            clearValue={() => clearFilter(GatewayFilterKeys.SYNC_RESPONSE_CONTENT)}
            placeholderLabel={t("pages.support.gatewayLogs.filters.syncResponseContent")}
          />
        </SearchBarFilterContainer>
      </FiltersContainer>

      <CustomRow>
        <SmallButton
          titleLabel={t(`pages.support.gatewayLogs.${showExtraFields ? "hide" : "show"}ExtraFields`)}
          onClick={() => setShowExtraFields(!showExtraFields)}
          iconComponent={
            <SmallButtonIconContainer color={styleTheme.palette.turquoiseBlue}>
              {showExtraFields ? <EyeCrossedIcon /> : <EyeIcon />}
            </SmallButtonIconContainer>
          }
        />
      </CustomRow>
      <Row style={{ marginBottom: 12 }}>
        <GatewayLogsCount>
          <span>{gatewayLogsCount}</span>
          &nbsp;
          {`${t("pages.support.gatewayLogs.recordsFound")}`}
        </GatewayLogsCount>
        <div style={{ marginRight: 24 }}>
          <SmallButton
            titleLabel={t("pages.support.gatewayLogs.refreshResults")}
            onClick={() => refreshResults()}
            iconComponent={
              <SmallButtonIconContainer color={styleTheme.palette.turquoiseBlue}>
                <RecurringIcon />
              </SmallButtonIconContainer>
            }
          />
        </div>
        <div>
          <SmallButton
            titleLabel={t("pages.support.gatewayLogs.clearFilters")}
            onClick={() => {
              setTimeValues({
                fromValue: "00:00",
                toValue: "18:00",
              });
              setTimeout(() => {
                setIsTimePickerOpen(false);
                clearFilter(GatewayFilterKeys.TIME_RANGE);
                resetFilters();
              }, 0);
            }}
            iconComponent={
              <SmallButtonIconContainer color={styleTheme.palette.vodafoneRed}>
                <UndoIcon />
              </SmallButtonIconContainer>
            }
          />
        </div>
      </Row>
    </>
  );
};

export default GatewayLogsFilters;

const TimePickerContainer = styled("div")`
  padding: 8px 8px 8px 12px;
`;

const ClearFilterText = styled("div")`
  font-family: Vodafone Rg;
  font-size: 14px;
  color: ${props => props.theme.palette.midGrey};
  text-align: left;
  display: flex;
  align-items: center;
  max-height: 30px;
  height: 30px;
  margin-top: 8px;
  margin-bottom: 8px;
  padding-left: 12px;
  &:hover {
    background: ${props => props.theme.palette.lightGrey};
  }
`;

const CustomRow = styled(Row)`
  margin-bottom: 18px;

  > ${RatioContainer} {
    margin-right: 24px;

    :last-of-type {
      margin-right: 0;
    }
  }
`;

const GatewayLogsCount = styled("div")`
  display: flex;
  flex-direction: row;
  margin-right: 24px;
  font-family: Vodafone Rg;
  font-size: 14px;
  color: ${props => props.theme.palette.midGrey};

  > span {
    font-weight: bold;
  }
`;

const SmallButtonIconContainer = styled("div")<{ color: string }>`
  width: 16px;
  height: 16px;
  stroke: ${props => props.color};

  svg {
    width: 16px;
    height: 16px;
    stroke: ${props => props.color};
  }
`;
